import * as React from 'react';
import {Box, Divider, useMediaQuery, useTheme} from '@mui/material';
import {Account} from '@toolpad/core/Account';
import type {SidebarFooterProps} from '@toolpad/core/DashboardLayout';

/**
 * Bottom-left sidebar footer housing the account/logout menu (moved out of the top-right
 * toolbar). Reuses Toolpad's `Account` component, which reads session/authentication from the
 * `AppProvider` context regardless of where it's rendered, so behavior (sign-out, etc.) is
 * unchanged - only its position moves. Falls back to the condensed avatar-only variant when the
 * sidebar is collapsed to its mini width.
 *
 * On mobile (see the mobile dashboard design reference) the account menu stays in the top-right
 * header instead (see DashboardToolbarActions), so this footer renders nothing there to avoid a
 * duplicate account menu inside the mobile temporary drawer.
 */
export function SidebarFooterAccount({mini}: SidebarFooterProps): React.ReactElement | null {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (isMobile) {
        return null;
    }

    return (
        <Box>
            <Divider/>
            <Account
                slotProps={{
                    preview: {variant: mini ? 'condensed' : 'expanded'},
                    // Default popover is anchored/arrowed for a top-right toolbar placement
                    // (opens downward, arrow on the right). The account now lives bottom-left in
                    // the sidebar footer, so flip both anchor/transform origins to open upward on
                    // the left, and move the arrow (the `&::before` pseudo-element) to the left
                    // edge so it still points back at the avatar.
                    popover: {
                        anchorOrigin: {vertical: 'top', horizontal: 'left'},
                        transformOrigin: {vertical: 'bottom', horizontal: 'left'},
                        slotProps: {
                            paper: {
                                sx: {
                                    mt: 0,
                                    mb: 1,
                                    '&::before': {
                                        top: 'auto',
                                        bottom: 0,
                                        left: 14,
                                        right: 'auto',
                                        transform: 'translateY(50%) rotate(45deg)',
                                    },
                                },
                            },
                        },
                    },
                }}
            />
        </Box>
    );
}
